import type { MovementId } from '../config/movements';
import { getMovement } from '../config/movements';
import type { RfidDocumentRow } from '../services/rfid.service';
import type {
  KitGroup,
  TransferDetails,
  TransportDetails,
  WizardLine,
} from '../types/wizard';
import { getReviewLineProgress } from './scanStats';
import type { KitBomItem } from './tagAllocation';
import { getDocumentPlant } from './document';

const MOVEMENT_REF_PREFIX: Partial<Record<MovementId, string>> = {
  allot: 'LO',
  custin: 'CI',
  dehire: 'DO',
  dehire_received: 'DI',
  relocation_out: 'RO',
  relocation_in: 'RI',
  transfer_out: 'TO',
  transfer_in: 'TI',
};

export const SUCCESS_COMPLETE_TITLES: Partial<Record<MovementId, string>> = {
  allot: 'Load Out Complete',
  custin: 'Customer In Complete',
  dehire: 'Dehire Out Complete',
  dehire_received: 'Dehire In Complete',
  relocation_out: 'Relocation Out Complete',
  relocation_in: 'Relocation In Complete',
  transfer_out: 'Transfer Out Complete',
  transfer_in: 'Transfer In Complete',
};

export type ScanLogRow = {
  tagId: string;
  material: string;
  kit: string;
  qty: string;
  time: string;
  vehicle: string;
};

export type SapSubmitDetails = {
  message: string;
  generatedDocument: string | null;
  materialDocument: string | null;
  trackingReference: string | null;
};

/** Parse SAP success message (e.g. created document + material document numbers). */
export function parseSapSubmitDetails(
  message: string,
  movementId?: MovementId,
  documentNo?: string
): SapSubmitDetails {
  const generatedDocument = message.match(/Document\s+(\d+)/i)?.[1]?.trim() ?? null;
  const materialDocument =
    message.match(/Material\s+Document\s+(\d+)/i)?.[1]?.trim() ?? null;
  const leapRef = message.match(/LEAP-[A-Z]+-\d{6,}-[\dA-Z]+/i)?.[0];
  const trackingReference =
    leapRef?.toUpperCase() ??
    (movementId ? buildReferenceNumber(movementId, documentNo, message) : null);
  return {
    message: message.trim(),
    generatedDocument,
    materialDocument,
    trackingReference,
  };
}

export type DocketRow = { label: string; value: string };
export type DocketSection = { title: string; rows: DocketRow[] };

function pushRow(rows: DocketRow[], label: string, value: unknown) {
  const v = String(value ?? '').trim();
  if (v) rows.push({ label, value: v });
}

function formatSapRaw(raw: unknown): string {
  if (raw == null) return '—';
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
}

/** Full movement docket for View Doc — document, transport, SAP, and scan totals. */
export function buildMovementDocketSections(input: {
  movementId: MovementId;
  document: RfidDocumentRow;
  transport: TransportDetails;
  transferDetails?: TransferDetails;
  effectiveDate: string;
  remarks: string;
  sap: SapSubmitDetails;
  sapRaw?: unknown;
  totalLines: number;
  fullyDispatched: number;
  tagsScanned: number;
  vehicle: string;
  dispatchTime: string;
  transferCustomerName?: string;
  transferToCustomerName?: string;
}): DocketSection[] {
  const movement = getMovement(input.movementId);
  const doc = input.document;
  const plant = getDocumentPlant(doc);

  const documentRows: DocketRow[] = [];
  pushRow(documentRows, 'Movement', movement.title);
  pushRow(documentRows, 'Sales / ref document', doc.DocumentNo);
  pushRow(documentRows, 'Customer', doc.CustName);
  pushRow(documentRows, 'Plant', plant);
  pushRow(documentRows, 'Receiver', doc.Receiver ?? doc.Customer);
  pushRow(documentRows, 'Posting date', doc.PostingDate);
  pushRow(documentRows, 'Effective date', input.effectiveDate);
  pushRow(documentRows, 'Remarks', input.remarks);

  const transportRows: DocketRow[] = [];
  pushRow(transportRows, 'Vehicle no.', input.transport.VEHICLE_NO || input.vehicle);
  pushRow(transportRows, 'Vehicle type', input.transport.VEHICLE_TYPE);
  pushRow(transportRows, 'Driver', input.transport.DRIVER_NAME);
  pushRow(transportRows, 'Driver mobile', input.transport.DRIVER_MOB_NO);
  pushRow(transportRows, 'Transporter', input.transport.TRANSPORTER_CODE);
  pushRow(transportRows, 'Transportation by', input.transport.TRANPORTATION_BY);
  pushRow(transportRows, 'LR no.', input.transport.LR_NO);
  pushRow(transportRows, 'Mode', input.transport.TRANSPORT_MODE);
  pushRow(transportRows, 'Distance', input.transport.distance);
  pushRow(transportRows, 'Dispatch time', input.dispatchTime);

  if (movement.wizardKind === 'transferOut' || movement.wizardKind === 'transferIn') {
    pushRow(transportRows, 'From customer', input.transferCustomerName);
    pushRow(transportRows, 'To customer', input.transferToCustomerName);
    if (input.transferDetails) {
      pushRow(transportRows, 'Invoice', input.transferDetails.invoiceNo);
      pushRow(transportRows, 'Location', input.transferDetails.location);
    }
  }

  const sapRows: DocketRow[] = [];
  pushRow(sapRows, 'Status', 'Success');
  pushRow(sapRows, 'Generated document', input.sap.generatedDocument);
  pushRow(sapRows, 'Material document', input.sap.materialDocument);
  pushRow(sapRows, 'Tracking reference', input.sap.trackingReference);
  pushRow(sapRows, 'SAP message', input.sap.message);

  const scanRows: DocketRow[] = [
    { label: 'SO lines', value: String(input.totalLines) },
    {
      label: 'Fully dispatched',
      value: `${input.fullyDispatched}/${input.totalLines}`,
    },
    { label: 'RFID tags scanned', value: String(input.tagsScanned) },
  ];

  const sections: DocketSection[] = [
    { title: 'Document', rows: documentRows },
    { title: 'Transport & dispatch', rows: transportRows },
    { title: 'SAP response', rows: sapRows },
    { title: 'Scan summary', rows: scanRows },
  ];

  if (input.sapRaw != null) {
    sections.push({
      title: 'SAP payload (posted / response)',
      rows: [{ label: 'JSON', value: formatSapRaw(input.sapRaw) }],
    });
  }

  return sections;
}

export function buildReferenceNumber(
  movementId: MovementId,
  documentNo?: string,
  apiMessage?: string
): string {
  const fromApi = apiMessage?.match(/LEAP-[A-Z]+-\d{6,}-[\dA-Z]+/i)?.[0];
  if (fromApi) return fromApi.toUpperCase();

  const prefix = MOVEMENT_REF_PREFIX[movementId] ?? 'DOC';
  const now = new Date();
  const ym = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
  const suffix = documentNo
    ? String(documentNo).replace(/\D/g, '').slice(-3).padStart(3, '0')
    : String(now.getDate()).padStart(3, '0');
  return `LEAP-${prefix}-${ym}-${suffix}`;
}

function kitLabelForLine(lineId: string, kitGroups: KitGroup[]): string {
  for (const g of kitGroups) {
    if (g.lines.some((l) => l.id === lineId)) {
      return g.kitMaterial || 'KIT';
    }
  }
  return '—';
}

function formatTagDisplay(tag: string): string {
  if (tag.length <= 14) return tag;
  return `${tag.slice(0, 6)}…${tag.slice(-4)}`;
}

function formatDispatchTime(d: Date): string {
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function buildScanLogRows(
  lines: WizardLine[],
  kitGroups: KitGroup[],
  transport: TransportDetails,
  dispatchTime: Date
): ScanLogRow[] {
  const vehicle = String(transport.VEHICLE_NO || '').trim();
  const vehicleShort = vehicle.length > 10 ? `${vehicle.slice(0, 8)}…` : vehicle || '—';
  const time = formatDispatchTime(dispatchTime);
  const rows: ScanLogRow[] = [];

  lines.forEach((line) => {
    if (!line.scannable || line.isKitHeader) return;
    const kit = kitLabelForLine(line.id, kitGroups);
    const material = line.materialDesc?.trim() || line.materialCode;
    line.rfidCodes.forEach((tag) => {
      rows.push({
        tagId: formatTagDisplay(tag),
        material,
        kit,
        qty: '1',
        time,
        vehicle: vehicleShort,
      });
    });
  });

  return rows;
}

export function scanLogToCsv(rows: ScanLogRow[]): string {
  const header = 'RFID Tag,Material,Kit,Qty,Time,Vehicle';
  const body = rows.map(
    (r) =>
      `"${r.tagId}","${r.material}","${r.kit}","${r.qty}","${r.time}","${r.vehicle}"`
  );
  return [header, ...body].join('\n');
}

export function computeDispatchSummary(
  lines: WizardLine[],
  kitGroups: KitGroup[],
  bom: KitBomItem[] | null,
  tagSet: Set<string>,
  variant: 'kit' | 'flat'
) {
  const progress = getReviewLineProgress(lines, kitGroups, bom, tagSet);
  const scannable = progress.filter((p) => p.line.scannable);
  const totalLines = scannable.length;
  const fullyDispatched = scannable.filter((p) => p.status === 'complete').length;
  return { totalLines, fullyDispatched };
}

/** True when /post_cust_out returned a draft (In Transit) success payload. */
export function isCustoutDraftSubmitResult(raw: unknown, message?: string): boolean {
  if (message && /draft saved|in transit/i.test(message)) return true;
  if (raw == null || typeof raw !== 'object') return false;
  const d = raw as Record<string, unknown>;
  const rd = d.Response_Data as Record<string, unknown> | undefined;
  const result = (rd?.Result ?? d.Result) as Record<string, unknown> | undefined;
  if (!result || typeof result !== 'object') return false;
  if (result.IsDraft === true) return true;
  const docNo = String(result.Document_No ?? result.DocumentNo ?? '');
  return docNo.startsWith('DRAFT-');
}
